import admin from 'firebase-admin'
import fs from 'fs/promises'
import path from 'path'
import { BaseStorageProvider } from './base.provider.js'
import type { FileToUpload, UploadedFile, DownloadedFile, FirebaseConfig } from '../types.js'
import { getFileSize, getTimestamp, sanitizePath } from '../utils.js'

/**
 * Firebase Storage provider
 */
export class FirebaseStorageProvider extends BaseStorageProvider {
  readonly name = 'firebase' as const
  private bucket?: any
  private config: FirebaseConfig
  private app?: admin.app.App

  constructor(config: FirebaseConfig, verbose: boolean = false) {
    super(verbose)
    this.config = config
  }

  async initialize(): Promise<void> {
    try {
      // Parse credentials if provided as string
      let credentials = this.config.credentials
      if (typeof credentials === 'string') {
        try {
          // Try to decode from base64 first (common in CI/CD)
          const decoded = Buffer.from(credentials, 'base64').toString('utf-8')
          // Check if decoded string looks like JSON
          if (decoded.startsWith('{')) {
            credentials = JSON.parse(decoded)
          } else {
            // Not base64, try parsing as direct JSON string
            credentials = JSON.parse(credentials)
          }
        } catch (error) {
          // If base64 decode fails, try parsing as direct JSON
          try {
            credentials = JSON.parse(credentials as string)
          } catch (jsonError) {
            const errorMsg = error instanceof Error ? error.message : String(error)
            throw new Error(`Invalid Firebase credentials JSON string. Error: ${errorMsg}`)
          }
        }
      }

      // Initialize Firebase Admin
      this.app = admin.initializeApp({
        credential: credentials
          ? admin.credential.cert(credentials as admin.ServiceAccount)
          : admin.credential.applicationDefault(),
        storageBucket: this.config.bucket
      })

      this.bucket = admin.storage().bucket(this.config.bucket)
      this.log(`Initialized Firebase Storage: ${this.config.bucket}`)
    } catch (error) {
      this.logError('Failed to initialize Firebase Storage', error as Error)
      throw error
    }
  }

  async uploadFile(file: FileToUpload): Promise<UploadedFile> {
    if (!this.bucket) {
      throw new Error('Firebase Storage not initialized')
    }

    const localPath = file.path
    const destination = file.destination || path.basename(localPath)
    const remotePath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(destination))
      : sanitizePath(destination)

    const size = await getFileSize(localPath)

    try {
      // Read file content
      const fileContent = await fs.readFile(localPath)

      // Upload to Firebase Storage
      const fileRef = this.bucket.file(remotePath)

      await fileRef.save(fileContent, {
        contentType: file.contentType,
        metadata: {
          metadata: file.metadata || {},
          cacheControl: 'public, max-age=31536000'
        },
        public: true // Make file publicly accessible
      })

      // Get public URL
      const [url] = await fileRef.getSignedUrl({
        action: 'read',
        expires: '03-01-2500' // Far future expiry for public access
      })

      this.log(`Uploaded ${localPath} → ${remotePath}`)

      return {
        localPath,
        remotePath,
        url,
        size,
        contentType: file.contentType,
        uploadedAt: getTimestamp()
      }
    } catch (error) {
      this.logError(`Failed to upload ${localPath}`, error as Error)
      throw error
    }
  }

  async downloadFile(remotePath: string): Promise<DownloadedFile> {
    if (!this.bucket) {
      throw new Error('Firebase Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      const fileRef = this.bucket.file(fullPath)

      // Download file content
      const [buffer] = await fileRef.download()

      // Get metadata
      const [metadata] = await fileRef.getMetadata()

      // Get public URL
      const [url] = await fileRef.getSignedUrl({
        action: 'read',
        expires: '03-01-2500'
      })

      this.log(`Downloaded ${remotePath} (${buffer.length} bytes)`)

      return {
        remotePath,
        buffer,
        url,
        size: buffer.length,
        contentType: metadata.contentType,
        downloadedAt: getTimestamp(),
        timeCreated: metadata.timeCreated,
        timeUpdated: metadata.updated,
        customMetadata: metadata.metadata
      }
    } catch (error) {
      this.logError(`Failed to download ${remotePath}`, error as Error)
      throw error
    }
  }

  async fileExists(remotePath: string): Promise<boolean> {
    if (!this.bucket) {
      throw new Error('Firebase Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      const [exists] = await this.bucket.file(fullPath).exists()
      return exists
    } catch (error) {
      this.logError(`Failed to check if file exists: ${remotePath}`, error as Error)
      return false
    }
  }

  async deleteFile(remotePath: string): Promise<void> {
    if (!this.bucket) {
      throw new Error('Firebase Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      await this.bucket.file(fullPath).delete()
      this.log(`Deleted ${remotePath}`)
    } catch (error) {
      this.logError(`Failed to delete ${remotePath}`, error as Error)
      throw error
    }
  }

  async listFiles(remotePath?: string): Promise<string[]> {
    if (!this.bucket) {
      throw new Error('Firebase Storage not initialized')
    }

    const prefix = remotePath
      ? this.config.basePath
        ? path.join(this.config.basePath, sanitizePath(remotePath))
        : sanitizePath(remotePath)
      : this.config.basePath || ''

    try {
      const [files] = await this.bucket.getFiles({ prefix })
      return files.map((file: any) => file.name)
    } catch (error) {
      this.logError('Failed to list files', error as Error)
      throw error
    }
  }

  async cleanup(): Promise<void> {
    if (this.app) {
      try {
        await this.app.delete()
        this.log('Firebase app cleaned up')
      } catch (error) {
        this.logError('Failed to clean up Firebase app', error as Error)
      }
    }
  }
}
